import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// An in-page link to a section (the verdict line, the skip link) scrolls there directly and moves focus, as a
// native anchor would — not through the URL, whose once-per-hash guard below would ignore a second click.
export const focusSection = (id: string): void => {
  const target = document.getElementById(id);
  if (!target) return;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  target.focus({ preventScroll: true });
};

// React Router doesn't scroll to a `#hash` on a client-side navigation, and a link from another page (Metas →
// `/dashboard#budget`) lands before the content above its target has loaded, so the target would move after the
// scroll. This waits until `ready`, then scrolls once per hash and moves focus there, as a native anchor would.
export const useScrollToHash = (ready: boolean): void => {
  const { hash } = useLocation();
  const scrolledFor = useRef<string>();

  useEffect(() => {
    if (!ready || !hash || scrolledFor.current === hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;

    scrolledFor.current = hash;
    target.scrollIntoView();
    target.focus({ preventScroll: true });
  }, [hash, ready]);
};
